import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Renderer2,
  NgZone,
} from '@angular/core';
import { Router } from '@angular/router';
import html2pdf from 'html2pdf.js';
import { PopupConfirmationComponent } from 'src/app/container/elements/popup-confirmation/popup-confirmation.component';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';

import { HttpIOService } from 'src/app/services/http-io.service';
import { v4 as uuidv4 } from 'uuid';
import { environment } from 'src/environments/environment';
import { DynamicSnacbarErrorComponent } from 'src/app/container/elements/dynamic-snacbar-error/dynamic-snacbar-error.component';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Helper } from 'src/app/helper/defult';

@Component({
  selector: 'app-cv-selector',
  templateUrl: './cv-selector.component.html',
  styleUrls: ['./cv-selector.component.scss'],
})
export class CvSelectorComponent implements OnInit {
  resumeData: any;
  templates = [
    { id: 'template1' },
    { id: 'template2' },
    { id: 'template3' },
    { id: 'template4' },
    { id: 'template5' },
    { id: 'template6' },
    { id: 'template7' },
    { id: 'template8' },
    { id: 'template9' },
    { id: 'template10' },
    { id: 'template11' },
    { id: 'template12' },
  ];
  selectedTemplate: string = 'template1';
  isProcessingPayment = false;
  transactionRef = `CVPAYMENT_${uuidv4()}`;
  paystackKey: string;

  @ViewChild('cvToDownload', { static: false }) cvToDownload!: ElementRef;

  constructor(
    private router: Router,
    private renderer: Renderer2,
    private dialog: MatDialog,
    private io: HttpIOService,
    private snackBar: MatSnackBar,
    private helper: Helper,
    private ngZone: NgZone
  ) {
    this.paystackKey = environment.paystack_key;
  }

  ngOnInit(): void {
    const state = history.state;
    if (state && state.resumeData) {
      this.resumeData = state.resumeData;
    } else {
      console.warn('No resumeData found in history state');
    }

    this.scrollToElement('topOfReview');
  }

  selectTemplate(templateId: string) {
    this.selectedTemplate = templateId;
  }

  scrollToElement(id: any) {
    setTimeout(() => {
      const element = <HTMLElement>document.querySelector(`#${id}`);
      element.scrollIntoView({ behavior: 'smooth' });
    });
  }

  // --- Event Handlers for the Paystack Directive ---

  /**
   * Called when the payment is successfully completed.
   * This is the most important handler.
   * @param response The response object from Paystack, which includes the transaction reference.
   */
  paymentDone(response: any): void {
    this.isProcessingPayment = true;

    const body = {
      path: '/payment-success',
      trxref: response.trxref,
    };

    //Notify your backend
    this.io.paymentSuccessPatch(body).subscribe((res) => {
      if (res.status === 'success') {
        // Trigger the download after successful payment
        this.downloadPDF();
      } else {
        this.snackBar
          .openFromComponent(DynamicSnacbarErrorComponent, {
            data: res.serverResponse.message,
            verticalPosition: 'top', // 'top' | 'bottom'
            horizontalPosition: 'right', //'start' | 'center' | 'end' | 'left' | 'right'
            duration: 6000,
          })
          .afterOpened()
          .subscribe(this.helper.setOnTop);
      }
      this.isProcessingPayment = false;
    });
  }

  /**
   * Called when the user closes the Paystack modal without completing the payment.
   */
  paymentCancel(): void {
    console.log('Payment cancelled by user.');
  }

  async downloadPDF(): Promise<void> {
    const container = this.cvToDownload?.nativeElement;
    if (!container) {
      console.error('CV container element (#cvToDownload) not found.');
      return;
    }

    const cvTemplateElement = container.children[0] as HTMLElement;
    if (!cvTemplateElement) {
      console.error(
        'CV template element to download was not found inside the container.'
      );
      return;
    }

    const options = {
      margin: [0.5, 0.2, 0.5, 0.2],
      filename: 'My_CV.pdf',
      image: { type: 'jpeg', quality: 1.0 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      },
      jsPDF: {
        unit: 'in',
        format: 'a4',
        orientation: 'portrait',
      },
    };

    // Check if we are in the mobile view using the same breakpoint as your SCSS
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      // --- MOBILE-ONLY LOGIC ---
      // This logic will only run on smaller screens

      const originalTransform = cvTemplateElement.style.transform;
      // Your mobile CSS applies `position: absolute` so we need to handle it
      const originalPosition = cvTemplateElement.style.position;

      // Temporarily remove scaling and position constraints for an accurate PDF capture
      this.renderer.setStyle(cvTemplateElement, 'transform', 'none');
      this.renderer.setStyle(cvTemplateElement, 'position', 'static');
      this.renderer.setStyle(container, 'height', 'auto');

      try {
        await html2pdf().from(cvTemplateElement).set(options).save();
        let successData = 'cv download success';

        this.ngZone.run(() => {
          this.dialog.closeAll();
          const dialogOpen = this.dialog.open(PopupConfirmationComponent, {
            height: 'auto',
            width: '450px',
            data: { data: successData },
          });
          dialogOpen.afterClosed().subscribe(() => {});
        });
      } catch (error) {
        console.error('Error generating PDF on mobile:', error);
      } finally {
        // ALWAYS restore the original styles to put the mobile UI back to normal
        this.renderer.setStyle(
          cvTemplateElement,
          'transform',
          originalTransform
        );
        this.renderer.setStyle(
          cvTemplateElement,
          'position',
          originalPosition || 'absolute'
        );
        this.renderer.setStyle(container, 'height', null); // Resets the inline style
        // for user coming from without cv flow
        localStorage.removeItem('redirectTo');
      }
    } else {
      // --- DESKTOP-ONLY LOGIC ---
      try {
        await html2pdf().from(cvTemplateElement).set(options).save();
        let successData = 'cv download success';

        this.ngZone.run(() => {
          this.dialog.closeAll();
          const dialogOpen = this.dialog.open(PopupConfirmationComponent, {
            height: 'auto',
            width: '450px',
            data: { data: successData },
          });
          dialogOpen.afterClosed().subscribe(() => {});
        });
      } catch (error) {
        console.error('Error generating PDF on desktop:', error);
      } finally {
        // for user coming from without cv flow
        if (localStorage.getItem('redirectTo')) {
          localStorage.removeItem('redirectTo');
        }
      }
      // No 'finally' block is needed here because we didn't change any styles.
    }
  }
}
